import { createIsomorphicFn } from '@tanstack/react-start';

import { buildHeadersProxy } from './build-headers-proxy';
import { ErrorFetch } from './errors';
import { resolveUpstream } from './resolve-upstream';

/**
 * On the client the nitro proxy keeps API calls same-origin, so we resolve
 * against `location.origin`. On the server there is no proxy hop and `location`
 * does not exist, so we resolve the upstream URL directly via the same rules
 * the proxy applies. The `.server` branch (and its `@server` imports) is
 * compiled out of the client bundle by TanStack Start.
 */
const resolveTarget = createIsomorphicFn()
  .client((url: string) => new URL(url, location.origin))
  .server((url: string) => new URL(resolveUpstream(url)));

/**
 * Referer the dev upstream requires (it rejects a missing/localhost referer).
 * Browsers forbid setting `Referer` and don't need it — the proxy adds it — so
 * the client branch is a no-op.
 */
const resolveHeadersProxy = createIsomorphicFn()
  .client((): Record<string, string> => ({}))
  .server(() => buildHeadersProxy());

type THandleFetchData = {
  url: string;
  query?: Record<string, string | undefined>;
  body?: unknown;
  signal?: AbortSignal;
};

type THandleFetchInit = {
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
  headers?: HeadersInit;
};

/**
 * Base wrapper over `fetch` for API requests.
 *
 * @param data.url - Path relative to `location.origin`
 * @param data.query - Query parameters (appended to the URL)
 * @param data.body - Request body (object → JSON, FormData → multipart)
 * @param data.signal - `AbortSignal` for request cancellation (pass from TanStack Query's queryFn)
 * @param init.method - HTTP method, defaults to GET
 * @param init.headers - Additional headers
 * @throws {ErrorFetch} When the response is not ok (4xx / 5xx)
 */
export const handleFetch = async <T>(
  { url, query, body, signal }: THandleFetchData,
  init?: THandleFetchInit,
): Promise<T> => {
  const target = resolveTarget(url);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        target.searchParams.set(key, value);
      }
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...resolveHeadersProxy(),
  };

  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers[key] = value;
    });
  }

  const isFormData = body instanceof FormData;

  if (body != null && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  let requestBody: BodyInit | undefined;
  if (isFormData) {
    requestBody = body;
  } else if (body != null) {
    requestBody = JSON.stringify(body);
  }

  const response = await fetch(target, {
    method: init?.method ?? 'GET',
    headers,
    body: requestBody,
    signal,
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ErrorFetch(response.status, response.statusText, data);
  }

  return data as T;
};
